import { clampNumber, isFiniteNumber } from './untils';

/**
 * Reorders a list by moving a single item from one index to another.
 * Returns the original list reference for no-op operations.
 */
export function moveItemInList<T>({
  items,
  fromIndex,
  toIndex,
}: {
  items: readonly T[];
  fromIndex: number;
  toIndex: number;
}): readonly T[] {
  if (!isFiniteNumber(fromIndex) || !isFiniteNumber(toIndex)) {
    return items;
  }

  if (items.length < 2) return items;

  const maxIndex = items.length - 1;

  if (fromIndex < 0 || fromIndex > maxIndex) {
    return items;
  }

  const clampedToIndex = clampNumber({
    value: toIndex,
    min: 0,
    max: maxIndex,
  });

  if (fromIndex === clampedToIndex) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);

  if (typeof movedItem === 'undefined') {
    return items;
  }

  nextItems.splice(clampedToIndex, 0, movedItem);

  return nextItems;
}

/**
 * Converts an insertion index to a destination index in the original list.
 * Useful when calculating final reorder position while the source item is still visible.
 */
export function getDestinationIndexFromInsertion({
  sourceIndex,
  insertionIndex,
  listLength,
}: {
  sourceIndex: number;
  insertionIndex: number;
  listLength: number;
}): number {
  if (
    !isFiniteNumber(sourceIndex) ||
    !isFiniteNumber(insertionIndex) ||
    !isFiniteNumber(listLength)
  ) {
    return 0;
  }

  if (listLength < 2) return sourceIndex;

  const maxIndex = listLength - 1;
  const clampedSourceIndex = clampNumber({
    value: sourceIndex,
    min: 0,
    max: maxIndex,
  });

  const clampedInsertionIndex = clampNumber({
    value: insertionIndex,
    min: 0,
    max: listLength,
  });

  const rawDestinationIndex =
    clampedInsertionIndex > clampedSourceIndex
      ? clampedInsertionIndex - 1
      : clampedInsertionIndex;

  return clampNumber({
    value: rawDestinationIndex,
    min: 0,
    max: maxIndex,
  });
}
